/**
 * English: Contacts
 *
 * What this file covers: The contact book: contacts, fields, tags, segments, imports, consent and erasing a
 * person's data.
 *
 * Tone: Clear and careful. Consent and erasure wording is legal in effect: say exactly what happens to the
 * person's data.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const contacts: Catalog = {
  'contacts.title': 'Contacts',
  'contacts.columns.locale': 'Locale',
  'contacts.search.label': 'Search',
  'contacts.search.placeholder': 'Search name, number or email',
  'contacts.shownOf': '{{count}} of {{total}}',
  'contacts.empty.hint': 'Add someone by hand, or import a CSV of your existing audience.',
  'contacts.emptySearch': 'Nothing matches “{{query}}”',
  'contacts.emptySearch.hint': 'This searches the whole contact book, not just the page you are on.',
  'contacts.filters.optIn': 'Opt-in status',
  'contacts.filters.optIn.any': 'Any consent',
  'contacts.filters.tag': 'Tag',
  'contacts.filters.tag.any': 'Any tag',
  'contacts.form.msisdnHint': 'Type it as you would dial it. We add the country code.',
  'contacts.form.localeHint': 'The language templates are sent in, and the one your chatbot uses with this person when it speaks it.',
  'contacts.form.localeUnset': 'Not set',
  'contacts.modal.delete.consent': 'This also erases {{msisdn}}’s opt-in and opt-out history. To stop messaging them while keeping the consent record, mark them opted out instead.',
  'contacts.toast.exported': 'Export downloaded.',
  'contacts.toast.exportFailed': 'Export failed',
  'contacts.import.title': 'Importing contacts',
  'contacts.import.uploading': 'Uploading {{file}}…',
  'contacts.import.queued': 'Queued, {{total}} rows.',
  'contacts.import.running': 'Importing… {{done}} of {{total}}',
  'contacts.import.stillRunning': 'Still importing. It will carry on in the background; refresh the list shortly.',
  'contacts.import.doneTitle': 'Import finished',
  'contacts.import.done': '{{inserted}} added, {{updated}} updated.',
  'contacts.import.doneWithSkips': '{{inserted}} added, {{updated}} updated, {{skipped}} skipped because the number could not be read.',
  'contacts.import.failedTitle': 'Import failed',
  'contacts.import.format.title': 'CSV format',
  'contacts.import.format.intro': 'The first row must be a header naming each column. These are the columns the importer knows; any other header is matched to a custom field.',
  'contacts.import.format.column': 'Column',
  'contacts.import.format.required': 'Required',
  'contacts.import.format.notes': 'Notes',
  'contacts.import.format.upsert': 'A row whose number already exists updates that contact rather than creating a second one. Blank cells leave the existing value alone.',
  'contacts.import.format.template': 'Download a template',
  'contacts.import.format.choose': 'Choose a file',
  'contacts.import.csv.col.msisdn': 'With the country code (+27821234567) or the way you dial it (0821234567). A number without a code takes the country you choose before importing.',
  'contacts.import.csv.col.name': 'Shown in the inbox instead of the number.',
  'contacts.import.csv.col.email': 'Optional. Not used for messaging.',
  'contacts.import.csv.col.locale': 'Two-letter code: en, af, zu, xh, st.',
  'contacts.import.csv.col.tags': 'Separate several tags with a vertical bar, e.g. vip|newsletter.',
  'contacts.optedIn': 'Opted in',
  'contacts.optedOut': 'Opted out',
  'contacts.description': 'Your WhatsApp contact book. Import a CSV or add contacts individually; opted-out contacts are excluded from broadcasts automatically.',
  'contacts.actions.importCsv': 'Import CSV',
  'contacts.loadError': 'Failed to load contacts.',
  'contacts.shown': '{{count}} shown',
  'contacts.empty': 'No contacts yet: import a CSV or add one.',
  'contacts.columns.msisdn': 'Phone number',
  'contacts.columns.optIn': 'Opt-in status',
  'contacts.optIn.in': 'Opted in',
  'contacts.optIn.out': 'Opted out',
  'contacts.modal.delete.title': 'Delete contact?',
  'contacts.modal.delete.body': 'Delete contact {{msisdn}}? This cannot be undone.',
  'contacts.toast.importQueued': 'Import queued (job {{jobId}}).',
  'contacts.toast.importQueuedTitle': 'CSV import',
  'contacts.toast.importFailed': 'Import failed',
  'contacts.toast.saved': 'Contact {{msisdn}} saved.',
  'contacts.drawer.new': 'New contact',
  'contacts.drawer.edit': 'Edit contact',
  'contacts.form.msisdnRequired': 'A phone number is required.',
  'contacts.form.tagsHint': 'Press Enter after each tag. Tags are how you group contacts for a broadcast.',
  'contacts.form.optIn': 'Opted in',
  'contacts.form.optInHint': 'Off = excluded from marketing broadcasts.',
  'contacts.view.notFound': 'Contact not found.',
  'contacts.view.header.noOptIn': 'no opt-in',
  'contacts.view.header.created': 'created {{when}}',
  'contacts.view.backToAll': 'All contacts',
  'contacts.view.tabs.profile': 'Profile',
  'contacts.view.tabs.consent': 'Consent',
  'contacts.view.consent.hint': 'Every recorded opt-in and opt-out for this person, newest first. This is the record to quote in a POPIA request.',
  'contacts.view.consent.empty': 'No consent events recorded',
  'contacts.view.consent.empty.hint': 'Contacts added before consent tracking, or imported in bulk, have no events. The current setting still applies.',
  'contacts.view.consent.error': 'The consent history could not be loaded.',
  'contacts.view.consent.entry': '{{channel}} · {{source}}',
  'contacts.view.consent.source.api': 'Changed in the console or through the API',
  'contacts.view.consent.source.stop_keyword': 'Replied STOP on WhatsApp',
  'contacts.view.consent.source.import': 'Set by a CSV import',
  'contacts.view.consent.source.unknown': 'Source not recorded',
  'contacts.view.profile.channels': 'Channels',
  'contacts.view.tabs.conversations': 'Conversations',
  'contacts.view.tabs.danger': 'Danger',
  'contacts.view.profile.locale': 'Locale',
  'contacts.view.profile.notOptedIn': 'not opted in',
  'contacts.view.profile.contactId': 'Contact ID',
  'contacts.view.tags.hint': 'Tags drive segmentation. Use them to scope broadcasts and route automations.',
  'contacts.view.tags.empty': 'No tags yet.',
  'contacts.view.tags.remove': 'Remove "{{tag}}"',
  'contacts.view.tags.addPlaceholder': 'e.g. vip',
  'contacts.view.tags.toast.added': 'Added tag "{{tag}}".',
  'contacts.view.tags.toast.removed': 'Removed tag "{{tag}}".',
  'contacts.view.tags.toast.updateFailed': 'Failed to update tags.',
  'contacts.view.conversations.hint': 'Inbox threads for {{msisdn}}. Open one to continue the chat or view history.',
  'contacts.view.conversations.empty': 'No conversations with this contact.',
  'contacts.view.conversations.noPreview': '(no preview)',
  'contacts.view.danger.optInHint': 'Marketing broadcasts require an active opt-in. Toggle off if the contact has requested to be removed from marketing channels.',
  'contacts.view.danger.markOptOut': 'Mark as opted out',
  'contacts.view.danger.markOptIn': 'Mark as opted in',
  'contacts.view.danger.deleteContact': 'Delete contact',
  'contacts.view.danger.deleteHint': 'Permanently removes {{msisdn}} from your contact list.',
  'contacts.view.danger.deleteButton': 'Delete contact…',
  'contacts.view.danger.modal.delete.title': 'Delete {{msisdn}}?',
  'contacts.view.danger.modal.delete.body': 'Permanently removes this contact and any segment memberships. Conversations and broadcast history with this number remain.',
  'contacts.view.danger.modal.delete.irreversible': 'This cannot be undone.',
  'contacts.view.danger.toast.markedOptOut': 'Marked opted out.',
  'contacts.view.danger.toast.markedOptIn': 'Marked opted in.',
  'contacts.view.danger.toast.optInFailed': 'Failed to update opt-in.',
  'contacts.view.danger.toast.deleted': 'Deleted {{msisdn}}.',
  'contacts.view.danger.toast.deleteFailed': 'Failed to delete contact.',
  'contacts.actions.import': 'Import',
  'contacts.actions.create': 'Create contact',
  'contacts.export.csv': 'Export as CSV',
  'contacts.export.xlsx': 'Export as Excel (.xlsx)',
  'contacts.columns.contact': 'Contact',
  'contacts.filters.clear': 'Clear filters',
  'contacts.filters.tag.option': '{{tag}} ({{count}})',
  'contacts.select.all': 'Select every contact on this page',
  'contacts.select.one': 'Select {{name}}',
  'contacts.rowsPerPage': 'Rows per page',
  'contacts.pagination': 'Pages',
  'contacts.pagination.previous': 'Previous page',
  'contacts.pagination.next': 'Next page',
  'contacts.pagination.page': 'Page {{page}}',
  'contacts.importCard.title': 'Import contacts',
  'contacts.importCard.body': 'Upload a CSV or an Excel file to add or update many contacts at once. Rows match on the phone number, so an import updates the people you already have.',
  'contacts.importCard.columns': 'Columns: msisdn, name, email, locale, tags, notes, plus one per custom field. Separate tags with a semicolon.',
  'contacts.importCard.template': 'Download template',
  'contacts.tags.add': 'Add a tag',
  'contacts.tags.remove': 'Remove the tag {{tag}}',
  'contacts.form.tab.details': 'Details',
  'contacts.form.tab.notes': 'Notes',
  'contacts.form.fullName': 'Full name',
  'contacts.form.fullName.placeholder': 'e.g. Mary Rodriguez',
  'contacts.form.phone': 'Phone number',
  'contacts.form.dialCode': 'Dialling code',
  'contacts.form.notes': 'Notes',
  'contacts.form.notesHint': 'Anything worth knowing about this person.',
  'contacts.form.notes.placeholder': 'Interested in the training programme. Prefers a call on weekday mornings.',
  'contacts.form.notes.private': 'Notes are internal. They are never sent to the contact.',
  'contacts.form.save': 'Save contact',
  'contacts.bulk.label': 'Actions for the selected contacts',
  'contacts.bulk.count': '{{count}} selected',
  'contacts.bulk.tag': 'Tag',
  'contacts.bulk.tagLabel': 'Tag to apply',
  'contacts.bulk.tagHint': 'Tags are how you group contacts. Send a broadcast to everyone carrying one.',
  'contacts.bulk.add': 'Add tag',
  'contacts.bulk.remove': 'Remove tag',
  'contacts.bulk.tagged': 'Tagged {{count}} contacts as {{tag}}.',
  'contacts.bulk.untagged': 'Removed {{tag}} from {{count}} contacts.',
  'contacts.bulk.broadcast': 'Send to a tag',
  'contacts.bulk.clear': 'Clear selection',
  'contacts.showingOf': 'Showing {{count}} of {{total}} contacts',
  'contacts.filters.title': 'Filters',
  'contacts.sortBy': 'Sort by {{column}}',
  'contacts.columns.optIn.hint': 'Whether this person may still be messaged. Opted-out contacts are excluded from every broadcast.',
  'contacts.drawer.view': 'Contact',
  'contacts.import.preview.title': 'Check the numbers before importing',
  'contacts.import.preview.reading': 'Reading {{file}}…',
  'contacts.import.preview.country': 'Country to assume',
  'contacts.import.preview.countryHint': 'For numbers written without a country code, such as 061 536 0809.',
  'contacts.import.preview.assumed': 'Assumed {{country}} ({{dial}}) for {{count}} numbers.',
  'contacts.import.preview.international': '{{count}} numbers already carried a country code and were kept.',
  'contacts.import.preview.failed': '{{count}} numbers could not be read and will be skipped.',
  'contacts.import.preview.allGood': 'Every number could be read.',
  'contacts.import.preview.row': 'Row',
  'contacts.import.preview.input': 'As written',
  'contacts.import.preview.reason': 'Why',
  'contacts.import.preview.moreFailed': 'and {{count}} more',
  'contacts.import.preview.sample': 'For example, {{input}} becomes {{e164}}.',
  'contacts.import.preview.confirm': 'Import {{count}} contacts',
  'contacts.import.preview.nothing': 'Nothing to import: no number in the file could be read.',
  'contacts.import.preview.failedToRead': 'The file could not be read.',
  'contacts.actions.message': 'Message',
  'contacts.bulk.message': 'Message',
  'contacts.fields.title': 'Contact fields',
  'contacts.fields.description': 'Extra details you keep about each contact beyond name, number and email. A field appears on the contact form, in the list filter and in segments, and travels with imports and exports.',
  'contacts.fields.new': 'New field',
  'contacts.fields.edit': 'Edit field',
  'contacts.fields.empty': 'No custom fields yet',
  'contacts.fields.emptyHint': 'Add a field for anything worth keeping about a person: an order number, a plan, a renewal date.',
  'contacts.fields.loadError': 'Could not load the contact fields.',
  'contacts.fields.columns.label': 'Label',
  'contacts.fields.columns.key': 'Key',
  'contacts.fields.columns.type': 'Type',
  'contacts.fields.columns.usage': 'Usage',
  'contacts.fields.columns.active': 'Status',
  'contacts.fields.usage': '{{count}} contacts',
  'contacts.fields.usage.one': '1 contact',
  'contacts.fields.active': 'Active',
  'contacts.fields.inactive': 'Hidden',
  'contacts.fields.type.text': 'Text',
  'contacts.fields.type.number': 'Number',
  'contacts.fields.type.date': 'Date',
  'contacts.fields.type.boolean': 'Yes / no',
  'contacts.fields.type.select': 'Choice',
  'contacts.fields.type.url': 'Web address',
  'contacts.fields.form.label': 'Label',
  'contacts.fields.form.labelHint': 'What the field is called on the contact form.',
  'contacts.fields.form.key': 'Key',
  'contacts.fields.form.keyHint': 'Follows the label until you change it: lower-case letters, digits and underscores. It names the column in imports and exports and the placeholder in messages.',
  'contacts.fields.form.keyLocked': 'The key cannot change once the field exists: every value stored on a contact is filed under it.',
  'contacts.fields.form.type': 'Type',
  'contacts.fields.form.typeHint': 'How the value is stored, and how filters and segments compare it.',
  'contacts.fields.form.typeLocked': 'The type cannot change once the field exists: values already stored would stop meaning what they meant.',
  'contacts.fields.form.options': 'Options',
  'contacts.fields.form.optionsHint': 'One per line. A contact holds exactly one of these.',
  'contacts.fields.form.optionsRequired': 'A choice field needs at least one option.',
  'contacts.fields.form.description': 'Description',
  'contacts.fields.form.descriptionHint': 'Shown under the field on the contact form.',
  'contacts.fields.form.active': 'Active',
  'contacts.fields.form.activeHint': 'Switching a field off hides it from the contact form and the export. Nothing stored on any contact is touched.',
  'contacts.fields.form.keyInvalid': 'Use lower-case letters, digits and underscores, starting with a letter.',
  'contacts.fields.form.keyReserved': 'Every contact already has a field with this name.',
  'contacts.fields.form.keyTaken': 'A field with this key already exists.',
  'contacts.fields.toast.created': 'Field “{{label}}” added',
  'contacts.fields.toast.updated': 'Field “{{label}}” saved',
  'contacts.fields.toast.deleted': 'Field “{{label}}” removed',
  'contacts.fields.toast.saveFailed': 'Could not save the field',
  'contacts.fields.toast.deleteFailed': 'Could not remove the field',
  'contacts.fields.delete.title': 'Remove “{{label}}”?',
  'contacts.fields.delete.body': '{{count}} contacts carry a value for this field. Removing it deletes those values from every one of them. This cannot be undone.',
  'contacts.fields.delete.bodyNone': 'No contact carries a value for this field yet.',
  'contacts.fields.delete.hint': 'To hide the field and keep the values, switch it off instead.',
  'contacts.fields.delete.confirm': 'Remove field',
  'contacts.form.tab.fields': 'Fields',
  'contacts.form.fields.empty': 'No custom fields yet',
  'contacts.form.fields.emptyHint': 'Define the extra details you keep about each contact and they appear here.',
  'contacts.form.fields.manage': 'Set up contact fields',
  'contacts.form.fields.manageHint': 'Missing a field?',
  'contacts.form.fields.unset': 'Not set',
  'contacts.form.fields.loadError': 'Could not load the contact fields.',
  'contacts.form.fields.datePlaceholder': 'YYYY-MM-DD',
  'contacts.form.fields.urlPlaceholder': 'https://',
  'contacts.form.fields.invalid.number': 'Enter a number.',
  'contacts.form.fields.invalid.date': 'Enter a date as YYYY-MM-DD.',
  'contacts.form.fields.invalid.url': 'Enter a full web address, starting with http:// or https://.',
  'contacts.form.fields.invalid.select': 'Choose one of the options.',
  'contacts.form.fields.invalid.boolean': 'Choose yes or no.',
  'contacts.form.fields.invalid.unknown': 'This field no longer exists. Reload and try again.',
  'contacts.filters.field': 'Field',
  'contacts.filters.field.any': 'Any field',
  'contacts.filters.field.value': 'Value',
  'contacts.filters.field.valueAny': 'Any value',
  'contacts.filters.field.yes': 'Yes',
  'contacts.filters.field.no': 'No',
  'contacts.filters.field.placeholder': 'Type a value',
  'contacts.import.preview.mapping': 'Columns',
  'contacts.import.preview.column': 'In the file',
  'contacts.import.preview.target': 'Goes to',
  'contacts.import.preview.target.ignored': 'Ignored',
  'contacts.import.preview.target.unknown': 'Matches nothing',
  'contacts.import.preview.target.field': 'Field: {{label}}',
  'contacts.import.preview.createFields': 'Create the {{count}} unmatched columns as text fields',
  'contacts.import.preview.createFieldsHint': 'Each becomes a custom field named after its header, and its values import with the rest. Leave this off to skip those columns.',
  'contacts.import.preview.fieldProblems': '{{count}} values do not fit their field\'s type. They will be kept as text.',
  'contacts.import.preview.fieldProblem': 'Row {{row}}, {{column}}: “{{value}}”',
  'contacts.import.doneFieldProblems': '{{count}} values did not fit their field\'s type and were kept as text.',
  'contacts.import.format.customFields': 'Any other column is matched to a custom field by its key or its label. Columns that match nothing can be created as text fields from the preview.',
  'contacts.import.csv.col.notes': 'Free-text notes kept against the contact.',
  'contacts.remove.action': 'Remove',
  'contacts.remove.title': 'Remove {{msisdn}} from your contacts?',
  'contacts.remove.body': 'Their name, email, tags, fields and notes are cleared and they are marked opted out, so no broadcast reaches them by accident. Conversations with them stay.',
  'contacts.remove.back': 'Adding the number again brings them back, still opted out. To delete everything you hold about them, use Erase this person instead.',
  'contacts.remove.confirm': 'Remove',
  'contacts.remove.toast': 'Removed {{msisdn}}. They stay opted out.',
  'contacts.remove.toastTitle': 'Removed',
  'contacts.remove.failed': 'Could not remove the contact.',
  'contacts.remove.cardTitle': 'Remove from your contacts',
  'contacts.remove.cardHint': 'Takes {{msisdn}} out of your list and opts them out. The number is kept so they are never messaged by accident.',
  'contacts.remove.button': 'Remove contact…',
  'contacts.erase.cardTitle': 'Erase this person',
  'contacts.erase.cardHint': 'For a request to delete their personal information under POPIA. Deletes the contact, their consent history, conversations, the files they sent, flow history, call recordings and transcripts, and takes their number off past broadcasts, sends and billing lines.',
  'contacts.erase.button': 'Erase this person…',
  'contacts.erase.modal.title': 'Erase {{msisdn}}',
  'contacts.erase.byNumber.title': 'Erase a phone number',
  'contacts.erase.byNumber.open': 'Erase a number…',
  'contacts.erase.modal.what': 'Everything you hold about this person is deleted: the contact, their consent history, conversations and the files they sent, flow history, call recordings and transcripts. Their number comes off past broadcasts, sends and billing lines, which keep their amounts.',
  'contacts.erase.modal.keep': 'Only a one-way code made from the number is kept, so they stay opted out if the number is added again. This cannot be undone.',
  'contacts.erase.modal.confirmLabel': 'Type their phone number to confirm',
  'contacts.erase.modal.confirmHint': 'Any spelling works, such as 082 123 4567 or +27 82 123 4567.',
  'contacts.erase.modal.numberLabel': 'Phone number',
  'contacts.erase.modal.numberHint': 'For someone who is not in your contacts: a person you removed, or who only ever messaged you.',
  'contacts.erase.modal.reasonLabel': 'Reason (optional)',
  'contacts.erase.modal.reasonHint': 'For your own record, for example "Request by email on 2 October". Leave out their name and number.',
  'contacts.erase.modal.submit': 'Erase permanently',
  'contacts.erase.mismatch': 'That is not this contact\'s number.',
  'contacts.erase.toast': 'Erased. Everything held about the person is being deleted.',
  'contacts.erase.toastPending': 'Erased here. The rest is deleted within a minute.',
  'contacts.erase.toastTitle': 'Erased',
  'contacts.erase.failed': 'Could not erase.',
  'contacts.form.smsOptIn': 'SMS consent',
  'contacts.form.smsOptIn.unset': 'Not recorded',
  'contacts.form.smsOptIn.yes': 'Agreed to SMS',
  'contacts.form.smsOptIn.no': 'Opted out of SMS',
  'contacts.form.smsOptInHint': 'Marketing SMS go only to people who agreed to SMS. Reminders and notices go to anyone not opted out. A STOP reply to an SMS opts the person out. WhatsApp consent does not count for SMS.',
  'contacts.sms.pill.yes': 'SMS: agreed',
  'contacts.sms.pill.no': 'SMS: opted out',
  'contacts.sms.pill.unset': 'SMS: not recorded',
  'contacts.import.csv.col.sms_opt_in': 'SMS consent: yes or no (true/false and 1/0 also work). A blank cell leaves it as it is. WhatsApp consent does not count for SMS.',
  'contacts.importCard.smsColumn': 'Add an sms_opt_in column (yes or no) to record SMS consent.',
  'contacts.form.keyRequired': 'Add a phone number or an email address.',
  'contacts.form.emailInvalid': 'This is not an email address.',
  'contacts.form.emailOptIn': 'Email consent',
  'contacts.form.emailOptIn.unset': 'Not recorded',
  'contacts.form.emailOptIn.yes': 'Agreed to email',
  'contacts.form.emailOptIn.no': 'Opted out of email',
  'contacts.form.emailOptInHint': 'Marketing email goes only to people who agreed to email. Notices go to anyone not opted out. An unsubscribe link in an email opts the person out. WhatsApp consent does not count for email.',
  'contacts.email.pill.yes': 'Email: agreed',
  'contacts.email.pill.no': 'Email: opted out',
  'contacts.email.pill.unset': 'Email: not recorded',
  'contacts.remove.cardHintEmail': 'Takes {{email}} out of your list.',
  'contacts.erase.modal.whatEmail': 'Everything you hold about this person is deleted: the contact, their consent history and their email conversations.',
  'contacts.erase.modal.keepEmail': 'Only a one-way code made from the address is kept, so they stay opted out if the address is added again. This cannot be undone.',
  'contacts.erase.modal.confirmEmailLabel': 'Type their email address to confirm',
  'contacts.erase.modal.confirmEmailHint': 'Capital letters do not matter.',
  'contacts.erase.mismatchEmail': 'That is not this contact\'s email address.',
  'contacts.erase.modal.numberOrEmailLabel': 'Phone number or email address',
  'contacts.erase.byNumber.titleEither': 'Erase a phone number or email address',
  'contacts.importCard.emailColumn': 'No phone numbers? An email column works on its own, and an email_opt_in column (yes or no) records email consent.',
  'contacts.import.format.either': 'Phone or email',
  'contacts.import.format.keyRule': 'A file needs a phone column (msisdn) or an email column, or both. A row with no number but with an email address becomes a contact with only an email address.',
  'contacts.import.csv.col.emailKey': 'An email address. Needed on a row that has no number.',
  'contacts.import.csv.col.email_opt_in': 'Email consent: yes or no (true/false and 1/0 also work; the column may also be called email_consent). A blank cell leaves it as it is. WhatsApp consent does not count for email.',
  'contacts.import.emailOnlyRows': '{{count}} contacts with only an email address.',
  'contacts.view.tabs.history': 'History',
  'contacts.changes.field.name': 'Name',
  'contacts.changes.field.email': 'Email address',
  'contacts.changes.field.locale': 'Language',
  'contacts.changes.noun.name': 'name',
  'contacts.changes.noun.email': 'email address',
  'contacts.changes.noun.locale': 'language',
  'contacts.changes.emptyValue': '(empty)',
  'contacts.changes.line.changed': '{{field}} changed from {{old}} to {{new}} on {{date}} by {{who}}.',
  'contacts.changes.line.cleared': '{{field}} cleared on {{date}} by {{who}}; it was {{old}}.',
  'contacts.changes.line.confirmed': 'The customer confirmed.',
  'contacts.changes.line.notAsked': 'The customer was not asked.',
  'contacts.changes.line.kept': 'The customer was asked to change their {{noun}} to {{new}} and said no; {{old}} stayed.',
  'contacts.changes.banner.title': 'Changes to check',
  'contacts.changes.banner.hint': 'What was there before is kept, so nothing is lost. The History tab lists every change.',
  'contacts.changes.undo': 'Undo',
  'contacts.changes.gotIt': 'Got it',
  'contacts.changes.undone': '{{field}} is back to {{value}}.',
  'contacts.changes.error.changedSince': 'This cannot be undone from here: it has been changed again since, to {{current}}.',
  'contacts.changes.error.changedSinceEmpty': 'This cannot be undone from here: it has been changed again since.',
  'contacts.changes.error.nothing': 'There is nothing to undo: this change did not replace anything.',
  'contacts.changes.error.notFound': 'That change is no longer on this contact.',
  'contacts.changes.error.role': 'Only the owner or an admin can undo a change.',
  'contacts.changes.error.load': 'The history could not be loaded.',
  'contacts.changes.who.flow': 'a flow',
  'contacts.changes.who.person': 'a team member',
  'contacts.changes.who.api': 'an integration',
  'contacts.changes.who.undo': 'Undo by {{person}}',
  'contacts.changes.history.hint': 'Every change to this contact\'s name, email address, language and custom fields, newest first, with what was there before.',
  'contacts.changes.history.empty': 'No changes yet',
  'contacts.changes.history.empty.hint': 'When a flow or someone on your team changes this contact\'s details, it shows here with what was there before.',
  'contacts.changes.history.fromTo': '{{old}} to {{new}}',
  'contacts.changes.history.kept': '{{old}} stayed; {{new}} was declined',
  'contacts.changes.history.by': 'By {{who}}',
  'contacts.changes.badge.confirmed': 'Customer confirmed',
  'contacts.changes.badge.declined': 'Customer said no',
  'contacts.changes.badge.unreviewed': 'Not checked yet',
  'contacts.drawer.openPage': 'Open full page',
  'contacts.page.description': 'Everything about this person: their details, conversations, consent and what changed.',
  'contacts.page.inList': 'Show in the list',
  'contacts.filters.language': 'Language',
  'contacts.filters.language.any': 'Any language'
};

export default contacts;
